import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingSalesOrderService from "../services/tradingSalesOrderService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: {
    margin: "7px 0 0",
    color: "#747b8d",
    fontSize: "13px",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "18px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "13px",
    marginBottom: "16px",
  },
  statLabel: {
    color: "#ffffff",
    fontSize: "11px",
    fontWeight: 700,
    marginBottom: "7px",
  },
  statValue: {
    fontSize: "27px",
    fontWeight: 750,
    lineHeight: 1.1,
    color: "#ffffff",
    fontVariantNumeric: "tabular-nums",
  },
  statCard: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    minHeight: "93px",
    boxSizing: "border-box",
    borderRadius: "12px",
    padding: "20px 16px",
    color: "#ffffff",
    boxShadow: "0 5px 12px rgba(15, 23, 42, 0.10)",
  },
  statAccentTotal: {
    background: "linear-gradient(115deg, #19a7f5 0%, #0860e8 100%)",
  },
  statAccentCurrent: {
    background: "linear-gradient(115deg, #a47bff 0%, #7738df 100%)",
  },
  statAccentPending: {
    background: "linear-gradient(115deg, #ffb51b 0%, #ff8500 100%)",
  },
  statAccentApproved: {
    background: "linear-gradient(115deg, #ff8998 0%, #e83e69 100%)",
  },
  statIcon: {
    width: "46px",
    height: "46px",
    minWidth: "46px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.23)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    color: "#ffffff",
    position: "relative",
    zIndex: 1,
  },
  statContent: {
    position: "relative",
    zIndex: 1,
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  input: {
    minWidth: "220px",
    flex: 1,
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "11px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    minWidth: "170px",
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 12px",
    background: "#fff",
    fontSize: "13px",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1100px",
  },
  th: {
    textAlign: "left",
    padding: "13px 12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
  },
  link: {
    color: "#5548cb",
    fontWeight: 700,
    cursor: "pointer",
    border: 0,
    padding: 0,
    background: "transparent",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: {
    color: "#858b9b",
    fontSize: "13px",
    textAlign: "center",
    padding: "32px 12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "15px",
    color: "#747b8d",
    fontSize: "12px",
  },
};

const normalizeStatus = (value) =>
  String(value || "DRAFT").trim().toUpperCase();

const formatStatus = (value) =>
  normalizeStatus(value).replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";

  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");

  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const formatAmount = (value, currency = "INR") => {
  const amount = Number(value ?? 0);

  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const statusColors = {
  DRAFT: {
    background: "#efedff",
    color: "#5548cb",
  },
  PENDING_APPROVAL: {
    background: "#fff4d6",
    color: "#946200",
  },
  APPROVED: {
    background: "#dcfce7",
    color: "#166534",
  },
  REJECTED: {
    background: "#fee2e2",
    color: "#b42318",
  },
  CANCELLED: {
    background: "#f1f3f9",
    color: "#5b6172",
  },
};

function StatusBadge({ status }) {
  const colors = statusColors[normalizeStatus(status)] || {
    background: "#f1f3f9",
    color: "#5b6172",
  };

  return (
    <span
      style={{
        display: "inline-block",
        borderRadius: "20px",
        padding: "5px 9px",
        fontSize: "11px",
        fontWeight: 700,
        whiteSpace: "nowrap",
        ...colors,
      }}
    >
      {formatStatus(status)}
    </span>
  );
}

export default function TradingSalesOrderListPage() {
  const navigate = useNavigate();

  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    pages: 1,
  });

  const pageSize = 10;

  const loadSalesOrders = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        per_page: pageSize,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (statusFilter !== "ALL") {
        params.status_code = statusFilter;
      }

      const response =
        await tradingSalesOrderService.getSalesOrders(params);

      const body = getResponseData(response);

      const rows = Array.isArray(body)
        ? body
        : Array.isArray(body?.items)
          ? body.items
          : [];

      setSalesOrders(rows);

      setPagination({
        total: Number(body?.total ?? rows.length),
        page: Number(body?.page ?? page),
        perPage: Number(body?.per_page ?? pageSize),
        pages: Math.max(1, Number(body?.pages ?? 1)),
      });
    } catch (err) {
      setSalesOrders([]);

      setPagination({
        total: 0,
        page: 1,
        perPage: pageSize,
        pages: 1,
      });

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Trading Sales Orders."
      );
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadSalesOrders();
  }, [loadSalesOrders]);

  const statuses = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "REJECTED",
    "CANCELLED",
  ];

  const pendingApproval = salesOrders.filter(
    (row) =>
      normalizeStatus(row.status_code) === "PENDING_APPROVAL"
  ).length;

  const approved = salesOrders.filter(
    (row) => normalizeStatus(row.status_code) === "APPROVED"
  ).length;

  const openSalesOrder = (row) => {
    if (row?.id) {
      navigate(`/trading/sales-orders/${row.id}`);
    }
  };

  const currentPage = pagination.page || page;
  const totalPages = Math.max(1, pagination.pages);

  const startIndex =
    pagination.total === 0
      ? 0
      : (currentPage - 1) * pagination.perPage + 1;

  const endIndex = Math.min(
    currentPage * pagination.perPage,
    pagination.total
  );

  const decorativeShape = {
    position: "absolute",
    width: "125px",
    height: "80px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)",
    right: "-24px",
    bottom: "-43px",
    transform: "rotate(-20deg)",
    pointerEvents: "none",
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Trading Sales Orders</h1>
          <p style={styles.subtitle}>
            View customer sales orders, track approval status, and monitor
            order values.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/quotations")}
          >
            View Quotations
          </button>

          <button
            type="button"
            style={styles.button}
            onClick={() => navigate("/trading/sales-orders/select-quotation")}
            >
            + Add Sales Order
        </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
          <button
            type="button"
            onClick={loadSalesOrders}
            style={{
              marginLeft: "12px",
              border: 0,
              background: "transparent",
              color: "#b42318",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.stats}>
        <div style={{ ...styles.statCard, ...styles.statAccentTotal }}>
          <div style={styles.statIcon}>
            <i className="fas fa-list-alt" />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statLabel}>Total Sales Orders</div>
            <div style={styles.statValue}>{pagination.total}</div>
          </div>
          <div style={decorativeShape} />
        </div>

        <div style={{ ...styles.statCard, ...styles.statAccentCurrent }}>
          <div style={styles.statIcon}>
            <i className="fas fa-file-alt" />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statLabel}>Orders on This Page</div>
            <div style={styles.statValue}>{salesOrders.length}</div>
          </div>
          <div style={decorativeShape} />
        </div>

        <div style={{ ...styles.statCard, ...styles.statAccentPending }}>
          <div style={styles.statIcon}>
            <i className="fas fa-hourglass-half" />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statLabel}>Pending Approval</div>
            <div style={styles.statValue}>{pendingApproval}</div>
          </div>
          <div style={decorativeShape} />
        </div>

        <div style={{ ...styles.statCard, ...styles.statAccentApproved }}>
          <div style={styles.statIcon}>
            <i className="fas fa-check-circle" />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statLabel}>Approved</div>
            <div style={styles.statValue}>{approved}</div>
          </div>
          <div style={decorativeShape} />
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search Sales Order no., quotation or reference..."
            style={styles.input}
            aria-label="Search Sales Orders"
          />

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            style={styles.select}
            aria-label="Filter Sales Orders by status"
          >
            <option value="ALL">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {formatStatus(status)}
              </option>
            ))}
          </select>

          <button
            type="button"
            style={{
              ...styles.button,
              opacity: loading ? 0.65 : 1,
            }}
            onClick={loadSalesOrders}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Sales Order No.</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Quotation No.</th>
                <th style={styles.th}>Order Date</th>
                <th style={styles.th}>Requested Delivery</th>
                <th style={styles.th}>Customer Reference</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Currency</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Net Amount</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={styles.muted}>
                    Loading Trading Sales Orders...
                  </td>
                </tr>
              ) : salesOrders.length === 0 ? (
                <tr>
                  <td colSpan={11} style={styles.muted}>
                    {error
                      ? "Sales Orders could not be loaded."
                      : "No Trading Sales Orders found."}
                  </td>
                </tr>
              ) : (
                salesOrders.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openSalesOrder(row)}
                      >
                        {row.sales_order_no || `SO-${row.id}`}
                      </button>
                    </td>

                    <td style={styles.td}>
                      {row.customer_name || row.business_partner_name || "—"}
                    </td>
                    <td style={styles.td}>
                      {row.quotation_no || "—"}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.sales_order_date)}
                    </td>

                    <td style={styles.td}>
                      {formatDate(row.requested_delivery_date)}
                    </td>

                    <td style={styles.td}>
                      {row.customer_reference || "—"}
                    </td>

                    <td style={styles.td}>
                      {row.quotation_version_no ?? "—"}
                    </td>

                    <td style={styles.td}>
                      {row.currency_code || "INR"}
                    </td>

                    <td style={styles.td}>
                      <StatusBadge status={row.status_code} />
                    </td>

                    <td style={styles.td}>
                      {formatAmount(row.net_amount, row.currency_code)}
                    </td>

                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openSalesOrder(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <span>
            Showing {startIndex}–{endIndex} of {pagination.total} records
          </span>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button
              type="button"
              disabled={loading || currentPage <= 1}
              onClick={() =>
                setPage((value) => Math.max(1, value - 1))
              }
              style={{
                ...styles.select,
                minWidth: "auto",
                cursor:
                  loading || currentPage <= 1
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  loading || currentPage <= 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              disabled={loading || currentPage >= totalPages}
              onClick={() =>
                setPage((value) =>
                  Math.min(totalPages, value + 1)
                )
              }
              style={{
                ...styles.select,
                minWidth: "auto",
                cursor:
                  loading || currentPage >= totalPages
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  loading || currentPage >= totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}